import { useEffect, useState } from "react";
import { api, formatApiError } from "@/api/client";
import { fmtDate } from "@/lib/constants";
import { CalendarClock, RefreshCw, Forward, CheckCircle2, Plus, X, Trash2, Pencil, BellRing, AlertTriangle, Eye, Download } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { BulkBar, RowCheckbox, bulkDelete } from "@/components/BulkBar";

const STATUS_STYLES = {
  pending:   "bg-slate-500/10 text-slate-500 border-slate-500/30",
  scheduled: "bg-blue-500/10 text-blue-500 border-blue-500/30",
  completed: "bg-emerald-500/10 text-emerald-500 border-emerald-500/30",
  skipped:   "bg-orange-500/10 text-orange-500 border-orange-500/30",
};

const QUARTERS = ["Q1", "Q2", "Q3", "Q4"];

export default function PMSchedules() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [reminderOpen, setReminderOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const q = statusFilter ? `?status=${statusFilter}` : "";
      const [p, c] = await Promise.all([api.get(`/pm${q}`), api.get("/customers")]);
      setItems(p.data); setCustomers(c.data);
    } catch (e) { setErr(formatApiError(e)); }
  };

  const loadReminders = async () => {
    try {
      const r = await api.get("/pm/reminders?within_days=7");
      setReminders(r.data);
      if (r.data.length > 0) {
        // Only auto-open once per browser session per user
        const key = "pm_reminder_dismissed_" + new Date().toISOString().slice(0, 10);
        if (!sessionStorage.getItem(key)) {
          setReminderOpen(true);
        }
      }
    } catch { /* silent — non-fatal */ }
  };

  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line
  useEffect(() => { loadReminders(); }, []); // eslint-disable-line

  const sync = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const r = await api.post("/pm/sync?days_ahead=7");
      setMsg(`${r.data.materialized} ticket(s) generated from PM schedules`);
      await load(); await loadReminders();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const skip = async (id) => {
    if (!window.confirm("Skip this PM visit?")) return;
    try { await api.post(`/pm/${id}/skip`); await load(); await loadReminders(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this PM schedule permanently?")) return;
    try { await api.delete(`/pm/${id}`); await load(); await loadReminders(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  const openReport = async (p, download = false) => {
    try {
      const r = await api.get(`/files/${p.report_document_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      if (download) {
        const el = document.createElement("a");
        el.href = url; el.download = p.report_document_name || `pm-report-${p.id}`;
        document.body.appendChild(el); el.click(); el.remove();
        URL.revokeObjectURL(url);
      } else {
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      }
    } catch (e) { setErr(formatApiError(e)); }
  };

  const ids = items.map((p) => p.id);
  const allSelected = ids.length > 0 && ids.every((id) => selected.has(id));
  const someSelected = ids.some((id) => selected.has(id));
  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allSelected) ids.forEach((id) => n.delete(id));
    else ids.forEach((id) => n.add(id));
    return n;
  });
  const clearSel = () => setSelected(new Set());
  const doBulkDelete = async () => {
    const idsArr = Array.from(selected);
    if (!window.confirm(`Delete ${idsArr.length} PM schedule(s)?`)) return;
    setBulkBusy(true);
    const ok = await bulkDelete(idsArr, (id) => api.delete(`/pm/${id}`));
    setBulkBusy(false); clearSel(); await load();
    if (ok < idsArr.length) setErr(`${idsArr.length - ok} deletion(s) failed.`);
  };

  const today = new Date().toISOString().slice(0, 10);
  const overdueCount = reminders.filter((r) => r.overdue).length;

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
      <header className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="label-eyebrow text-primary">Automation</p>
          <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight">Preventive Maintenance</h1>
          <p className="mt-1 text-sm text-muted-foreground">Quarterly PM visits with PO tracking and due-date reminders.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {reminders.length > 0 && (
            <button
              data-testid="pm-reminders-open"
              onClick={() => setReminderOpen(true)}
              className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-xs font-semibold ${overdueCount ? "border-rose-500 bg-rose-500/10 text-rose-600" : "border-amber-500 bg-amber-500/10 text-amber-600"}`}
            >
              <BellRing className="h-3.5 w-3.5" />
              {reminders.length} due
              <span className="ml-1 rounded-full bg-current px-1.5 py-0.5 text-[10px] font-bold text-white">{reminders.length}</span>
            </button>
          )}
          <button
            data-testid="pm-create-button"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-3.5 w-3.5" /> New PM visit
          </button>
          <button
            data-testid="pm-sync-button"
            disabled={busy}
            onClick={sync}
            className="inline-flex items-center gap-1.5 rounded-md border border-border/70 px-3 py-2 text-xs font-semibold hover:bg-secondary disabled:opacity-60"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
            {busy ? "Materializing…" : "Sync now (next 7 days)"}
          </button>
        </div>
      </header>

      <div className="mb-4 flex items-center gap-2">
        <select
          data-testid="pm-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="scheduled">Scheduled (ticket created)</option>
          <option value="skipped">Skipped</option>
        </select>
      </div>

      {err && <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
      {msg && (
        <div className="mb-3 inline-flex items-center gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-500">
          <CheckCircle2 className="h-3.5 w-3.5" /> {msg}
        </div>
      )}

      <div className="sharp-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-border/60 bg-secondary/30 text-left">
            <tr>
              <th className="w-10 px-4 py-2">
                <RowCheckbox testid="pm-select-all" checked={allSelected} indeterminate={!allSelected && someSelected} onChange={toggleAll} />
              </th>
              <th className="px-4 py-2 label-eyebrow">Due date</th>
              <th className="px-4 py-2 label-eyebrow">Customer</th>
              <th className="px-4 py-2 label-eyebrow">Quarter</th>
              <th className="px-4 py-2 label-eyebrow">PM period</th>
              <th className="px-4 py-2 label-eyebrow">PO No</th>
              <th className="px-4 py-2 label-eyebrow">Report</th>
              <th className="px-4 py-2 label-eyebrow">Status</th>
              <th className="px-4 py-2 label-eyebrow">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => {
              const overdue = p.due_date && p.due_date < today && p.status === "pending";
              return (
                <tr key={p.id} data-testid={`pm-row-${p.id}`} className="border-b border-border/40 hover:bg-secondary/40">
                  <td className="px-4 py-2">
                    <RowCheckbox testid={`pm-select-${p.id}`} checked={selected.has(p.id)} onChange={() => toggleOne(p.id)} />
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <CalendarClock className={`h-3.5 w-3.5 ${overdue ? "text-red-500" : "text-muted-foreground"}`} />
                      <span className={overdue ? "font-semibold text-red-500" : ""}>{fmtDate(p.due_date)}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2">{p.customer_name || "—"}</td>
                  <td className="px-4 py-2">
                    {p.quarter ? (
                      <span className="inline-flex rounded-md bg-primary/10 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-primary">{p.quarter}</span>
                    ) : (
                      <span className="text-xs text-muted-foreground">{p.visit_no ? `Visit ${p.visit_no}${p.of_total ? ` / ${p.of_total}` : ""}` : "—"}</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">
                    {p.period_from || p.period_to ? (
                      <span>{fmtDate(p.period_from) || "—"} → {fmtDate(p.period_to) || "—"}</span>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{p.po_no || "—"}</td>
                  <td className="px-4 py-2">
                    {p.report_document_id ? (
                      <div className="flex items-center gap-1">
                        <button data-testid={`pm-report-view-${p.id}`} title={`View report${p.report_document_name ? `: ${p.report_document_name}` : ""}`} onClick={() => openReport(p, false)} className="rounded-md border border-border/70 p-1 text-muted-foreground hover:text-primary"><Eye className="h-3.5 w-3.5" /></button>
                        <button data-testid={`pm-report-download-${p.id}`} title="Download report" onClick={() => openReport(p, true)} className="rounded-md border border-border/70 p-1 text-muted-foreground hover:text-primary"><Download className="h-3.5 w-3.5" /></button>
                      </div>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-2">
                    <span className={`inline-flex rounded-md border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${STATUS_STYLES[p.status] || STATUS_STYLES.pending}`}>
                      {p.status}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex gap-1">
                      <button data-testid={`pm-edit-${p.id}`} onClick={() => setEditing(p)} className="inline-flex items-center gap-1 rounded-md border border-border/70 px-2 py-1 text-xs text-muted-foreground hover:text-primary"><Pencil className="h-3 w-3" /></button>
                      {p.ticket_id && (
                        <button data-testid={`pm-open-ticket-${p.id}`} onClick={() => navigate(`/app/tickets/${p.ticket_id}`)} className="inline-flex items-center gap-1 rounded-md border border-border/70 px-2 py-1 text-xs text-primary">
                          <Forward className="h-3 w-3" /> Open ticket
                        </button>
                      )}
                      {p.status === "pending" && (
                        <button data-testid={`pm-skip-${p.id}`} onClick={() => skip(p.id)} className="inline-flex items-center gap-1 rounded-md border border-border/70 px-2 py-1 text-xs text-muted-foreground hover:text-orange-500">Skip</button>
                      )}
                      <button data-testid={`pm-delete-${p.id}`} onClick={() => del(p.id)} className="inline-flex items-center gap-1 rounded-md border border-border/70 px-2 py-1 text-xs text-muted-foreground hover:text-destructive"><Trash2 className="h-3 w-3" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-xs text-muted-foreground">No PM schedules yet. Click &quot;New PM visit&quot; to add one.</td></tr>}
          </tbody>
        </table>
      </div>

      <BulkBar count={selected.size} entityLabel="PM schedules" onClear={clearSel} onDelete={doBulkDelete} busy={bulkBusy} />

      {creating && <PMModal customers={customers} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); loadReminders(); }} />}
      {editing && <PMModal existing={editing} customers={customers} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); loadReminders(); }} />}

      {reminderOpen && reminders.length > 0 && (
        <ReminderPopup
          reminders={reminders}
          onClose={() => {
            setReminderOpen(false);
            const key = "pm_reminder_dismissed_" + new Date().toISOString().slice(0, 10);
            sessionStorage.setItem(key, "1");
          }}
        />
      )}
    </div>
  );
}

function ReminderPopup({ reminders, onClose }) {
  const overdue = reminders.filter((r) => r.overdue);
  const upcoming = reminders.filter((r) => !r.overdue);
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-lg sharp-card max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between border-b border-border/60 px-5 py-3">
          <div className="flex items-start gap-2">
            <BellRing className="mt-0.5 h-5 w-5 text-amber-500" />
            <div>
              <p className="label-eyebrow text-amber-500">Reminder</p>
              <h3 className="mt-0.5 font-heading text-lg font-semibold">PM visits need attention</h3>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {overdue.length > 0 && <><b className="text-rose-600">{overdue.length} overdue</b> · </>}
                <span>{upcoming.length} due within 7 days</span>
              </p>
            </div>
          </div>
          <button data-testid="pm-reminder-close" onClick={onClose} className="rounded-md border border-border/70 p-1.5"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
          {reminders.slice(0, 20).map((r) => (
            <div key={r.id} className={`flex items-start justify-between gap-2 rounded-md border px-3 py-2 text-xs ${r.overdue ? "border-rose-500/40 bg-rose-500/5" : "border-border/60"}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  {r.overdue && <AlertTriangle className="h-3 w-3 text-rose-500" />}
                  <span className="font-medium text-foreground">{r.customer_name || "Unknown"}</span>
                  {r.quarter && <span className="rounded-sm bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold uppercase text-primary">{r.quarter}</span>}
                </div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">
                  Due <b className={r.overdue ? "text-rose-600" : ""}>{r.due_date}</b>
                  {r.po_no && <> · PO <span className="font-mono">{r.po_no}</span></>}
                </div>
              </div>
            </div>
          ))}
          {reminders.length > 20 && <p className="pt-1 text-center text-[11px] text-muted-foreground">…and {reminders.length - 20} more</p>}
        </div>
        <div className="border-t border-border/60 px-5 py-3 text-right">
          <button data-testid="pm-reminder-dismiss" onClick={onClose} className="rounded-md bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90">
            Dismiss for today
          </button>
        </div>
      </div>
    </div>
  );
}

function PMModal({ customers, onClose, onSaved, existing }) {
  const [form, setForm] = useState({
    customer_id: existing?.customer_id || "",
    quarter: existing?.quarter || "",
    period_from: existing?.period_from || "",
    period_to: existing?.period_to || "",
    po_no: existing?.po_no || "",
    due_date: existing?.due_date || "",
    notes: existing?.notes || "",
    report_document_id: existing?.report_document_id || "",
  });
  const [reportName, setReportName] = useState(existing?.report_document_name || "");
  const [uploadingReport, setUploadingReport] = useState(false);
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const uploadReport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingReport(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "document");
      fd.append("folder", "PM Reports");
      const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, report_document_id: r.data.id }));
      setReportName(file.name);
    } catch (er) { setErr(formatApiError(er)); }
    finally { setUploadingReport(false); }
  };

  const openReport = async (download = false) => {
    try {
      const r = await api.get(`/files/${form.report_document_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      if (download) {
        const el = document.createElement("a");
        el.href = url; el.download = reportName || "pm-report";
        document.body.appendChild(el); el.click(); el.remove();
        URL.revokeObjectURL(url);
      } else {
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      }
    } catch (er) { setErr(formatApiError(er)); }
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const payload = { ...form, report_document_name: reportName || null };
      // Strip empty strings so backend stores null / stays untouched
      ["quarter", "period_from", "period_to", "po_no", "notes", "report_document_id", "report_document_name"].forEach((k) => {
        if (!payload[k]) delete payload[k];
      });
      if (existing) await api.patch(`/pm/${existing.id}`, payload);
      else await api.post("/pm", payload);
      onSaved();
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl sharp-card max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3 sticky top-0 bg-card z-10">
          <h3 className="font-heading text-lg font-semibold">{existing ? "Edit PM visit" : "New PM visit"}</h3>
          <button data-testid="pm-modal-close" onClick={onClose} className="rounded-md border border-border/70 p-1.5"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={submit} className="grid gap-3 p-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="label-eyebrow mb-1.5 block">Customer *</label>
            <select data-testid="pm-form-customer" required value={form.customer_id} onChange={set("customer_id")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="">Select customer…</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Quarter</label>
            <select data-testid="pm-form-quarter" value={form.quarter} onChange={set("quarter")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="">— Select —</option>
              {QUARTERS.map((q) => <option key={q} value={q}>{q}</option>)}
            </select>
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">PO No</label>
            <input data-testid="pm-form-po" value={form.po_no} onChange={set("po_no")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="e.g. PO-2025-0042" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">PM period — From</label>
            <input data-testid="pm-form-period-from" type="date" value={form.period_from} onChange={set("period_from")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">PM period — To</label>
            <input data-testid="pm-form-period-to" type="date" value={form.period_to} onChange={set("period_to")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div className="sm:col-span-2">
            <label className="label-eyebrow mb-1.5 block">Due date *</label>
            <input data-testid="pm-form-due" required type="date" value={form.due_date} onChange={set("due_date")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
            <p className="mt-1 text-[10px] text-muted-foreground">A reminder popup will show automatically 7 days before this date.</p>
          </div>
          <div className="sm:col-span-2">
            <label className="label-eyebrow mb-1.5 block">PM Report (upload)</label>
            <input data-testid="pm-form-report" type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" onChange={uploadReport} className="block w-full text-xs" />
            {uploadingReport && <p className="mt-1 text-[11px] text-muted-foreground">Uploading…</p>}
            {form.report_document_id && !uploadingReport && (
              <div className="mt-1 flex items-center gap-2">
                <p className="text-[11px] text-emerald-500">✓ Report: {reportName || form.report_document_id}</p>
                <button type="button" data-testid="pm-report-view" onClick={() => openReport(false)} className="rounded-md border border-border/70 px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground hover:text-primary">View</button>
                <button type="button" data-testid="pm-report-download" onClick={() => openReport(true)} className="rounded-md border border-border/70 px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground hover:text-primary">Download</button>
              </div>
            )}
          </div>
          <div className="sm:col-span-2">
            <label className="label-eyebrow mb-1.5 block">Notes / checklist</label>
            <textarea data-testid="pm-form-notes" value={form.notes} onChange={set("notes")} rows={3} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none" />
          </div>
          {err && <div className="sm:col-span-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
          <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-md border border-border/70 px-4 py-2 text-sm">Cancel</button>
            <button data-testid="pm-form-submit" disabled={busy} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
              {busy ? "Saving…" : (existing ? "Save changes" : "Create PM")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
